import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGetLightUsersQuery } from '../../store/redux/reducers/usersSlice'
import { RequestProps, User } from '../../store/types'
import { AutocompleteFetch } from '../Autocomplete/AutocompleteFetch'

interface AutocompleteEmployeesProps {
  selectedEmployees: User[]
  setSelectedEmployees: (employees: User[]) => void
  currentUser: User | null
  isSendToAll?: boolean
  setIsSendToAll?: (value: boolean) => void
  isDeselectAll?: boolean
  setIsDeselectAll?: (value: boolean) => void
}

const AutocompleteEmployees: React.FC<AutocompleteEmployeesProps> = ({
  selectedEmployees,
  setSelectedEmployees,
  currentUser,
  isSendToAll,
  setIsSendToAll,
  isDeselectAll,
  setIsDeselectAll
}) => {
  const [page, setPage] = useState(0)
  const [displayedEmployees, setDisplayedEmployees] = useState<Map<string, User>>(new Map())
  const [searchTerm, setSearchTerm] = useState<string>('')
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const employeeProps = useMemo(() => {
    const defaultProps: RequestProps = {
      page: page,
      size: 20,
      'query:firstName,lastName': searchTerm,
    }
    return defaultProps
  }, [page, searchTerm])

  const { data: employeesData } = useGetLightUsersQuery(employeeProps, {
    skip: !currentUser,
  })

  useEffect(() => {
    if (employeesData) {
      setDisplayedEmployees(prev => {
        const newEmployees = employeesData.content.filter(user => !prev.has(user.id))
        const updatedMap = new Map(prev)
        newEmployees.forEach(user => updatedMap.set(user.id, user))
        return updatedMap
      })
    }
  }, [employeesData, page, searchTerm])

  useEffect(() => {
    if (employeesData?.content) {
      if (!employeesData.last) {
        setPage(prev => prev + 1)
      }
    }
  }, [employeesData])

  useEffect(() => {
    if (isSendToAll) {
      if (employeesData?.last) {
        setSelectedEmployees(Array.from(displayedEmployees.values()))
      }
    } else if (isDeselectAll) {
      setSelectedEmployees([])
    }
  }, [isSendToAll, employeesData, displayedEmployees, setSelectedEmployees, isDeselectAll])

  const handleSearchInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      const newSearchTerm = event.target.value
      setSearchTerm(newSearchTerm)
      setPage(0)
      if (newSearchTerm === '') {
        setDisplayedEmployees(new Map())
      }
    }, 500)
  }, [])

  const options = useMemo(() => {
    const employeeList = Array.from(displayedEmployees.values())
    return currentUser && !displayedEmployees.has(currentUser.id)
      ? [...employeeList, currentUser]
      : employeeList
  }, [displayedEmployees, currentUser])

  useEffect(() => {
    if (!setIsSendToAll || !setIsDeselectAll) return

    const allEmployeesCount = options.length
    if (selectedEmployees.length < allEmployeesCount) {
      setIsSendToAll(false)
    } else if (selectedEmployees.length === allEmployeesCount) {
      setIsSendToAll(true)
      setIsDeselectAll(false)
    }
  }, [options.length, selectedEmployees.length, setIsDeselectAll, setIsSendToAll])

  return (
    <AutocompleteFetch<User>
      selectedItems={selectedEmployees}
      setSelectedItems={setSelectedEmployees}
      options={options}
      getLabel={(user) => `${user.firstName} ${user.lastName}`}
      getId={(user) => user.id}
      labelTranslationKey="components.notificationSidebar.administrativeNotification.selectEmployee"
      onSearchChange={handleSearchInputChange}
    />
  )
}

export default AutocompleteEmployees